import {
  // Card wrapper + its measured chrome (single source next to the
  // markup it measures — bi-components shared.jsx / maps.jsx).
  ChartCard,
  InfoTip,
  CHART_CARD_CHROME_PX,
  Callout,
  MetaGrid,
  MetricStrip,
  RankedList,
  toneStyle,
  TONES,
  resolveTone,
  MeterBar,
  StackedShareBar,
  ShareBreakdown,
  IndexMeter,
  formatValue,
  MOTION,
  SEMANTIC,
  ToneIcon,
  fmtN,
  useEntrance,
  useGrowCycle,
  lineDataSig,
  usePrefersReducedMotion,
  MAP_CARD_CHROME_PX,
  useColorTheme,
  // Static charts (no hover tooltip variant)
  RadarChart,
  WaterfallChart,
  FunnelChart,
  TreemapChart,
  HeatmapChart,
  BulletChart,
  LollipopChart,
  StackedArea,
  StackedBar,
  DivergingStackedBar,
  BarTrend,
  LineTrend,
  // Interactive (hover tooltips)
  VerticalBarT,
  HorizontalBarT,
  LineChartT,
  MultiLineT,
  PieChartT,
  GroupedBarT,
  ComboChartT,
  StackedShareComboT,
  ScatterChartT,
  BubbleChartT,
  // Geo + temporal-density
  SouthAmericaMap,
  ChileMap,
  CalendarHeatmap,
  // Phase G — analytics widget
  TimelineChart,
  // Redesign — trend-matrix cell (fiscal comparison)
  parseValueDelta,
  parseDeltaOnly,
  DeltaChip,
  TrendCell,
  trendMetricColor,
  type ParsedValueDelta,
  type Tone,
} from "@pervasivemind/bi-components";
import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  type DataFormat,
  formatCellValue,
  formatNumberLocale,
  parseCurrencyFormat,
  toNumber,
} from "./formatting";
import { MessageCard } from "./MessageCard";
import { ResponsiveCardGrid } from "./ResponsiveCardGrid";
import { NumberTween } from "./motion/NumberTween";
import { parseFormattedNumber } from "./motion/formatted-number";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { pickLang, useLang, type Lang } from "./i18n-binding";

/**
 * Table column config shape consumed by ``GenericTableChart``.
 * Locally defined here so the package does not depend on a specific
 * consumer's ``@/lib/api`` module.
 */
export interface ReportTableColumnConfig {
  key: string;
  /**
   * SQL column name when distinct from ``key``. Optional because chart
   * mini-tables and Preview-generated configs frequently omit it (the
   * key doubles as the column name). Only ``DataTable``-style consumers
   * actually need this — ``GenericTableChart`` reads ``key`` only.
   */
  column?: string;
  label: string;
  format?: string | null;
  badge?: string | null;
  /** Redesign — per-column cell render mode. ``"trend"`` turns SQL
   * "value ▲/▼ ±x.x%" strings into bold value + micro-bar + pastel
   * delta chip; non-parseable cells keep today's rendering. */
  render?: string | null;
}

/**
 * Pixel-identical to ``ai-reporting-builder/frontend/src/components/dashboard/GenericChart.tsx``.
 *
 * Replaced ECharts in May 2026 with the Omnitok-branded bi-components SVG
 * library (`@pervasivemind/bi-components`). The dispatch table maps
 * `props.type` (the value persisted in `dashboard_config::charts`)
 * to the matching component:
 *
 *   bar / bar_horizontal / line / pie / grouped_bar (stacked or not)
 *   scatter / bubble / table  ← the original 8 types, drop-in
 *
 *   radar / waterfall / funnel / treemap / heatmap / bullet / lollipop
 *   combo / bar_trend / line_trend / multi_line / stacked_area /
 *   stacked_bar  ← new types added by Phase 3 (see ChartTypeSelector)
 *
 * Charts whose `props.data` shape doesn't fit the expected bi-components
 * input render an empty state via the lib's internal `hasData()`
 * guard; the wrapper here covers a card + title + error state.
 */
/** Editor-driven data-label config (raw / compact / currency / percent + decimals). */
export interface DataLabelsConfig {
  show: boolean;
  format?: "raw" | "compact" | "currency" | "percent";
  decimals?: number;
}

/** Editor-driven axis-format config. Applies per-axis on Cartesian charts. */
export interface AxisFormatConfig {
  format?: "raw" | "compact" | "currency" | "percent";
  decimals?: number;
  /** Lock the axis scale (min/max). When omitted, the chart auto-fits. */
  min?: number;
  max?: number;
  /** Tick step for the axis (e.g. 10 → 0,10,20,...). */
  step?: number;
  /** Suffix appended to tick labels (e.g. "%", "sem."). */
  suffix?: string;
}

interface GenericChartProps {
  type: string;
  title: string;
  data: Array<Record<string, unknown>>;
  error?: string;
  /**
   * Uniform-grid PR-2 — the pixel height of the GRID CELL this chart
   * occupies (`cellPx(rowSpan)` from `grid-layout.ts`, computed by
   * `DashboardBody` from the placement simulator's FINAL row span).
   * When set, the card fills the cell (`ChartCard fill`) and the SVG
   * height is derived arithmetically (`cellPx − CHART_CARD_CHROME_PX`) — no
   * ResizeObserver, no reflow loops. When ABSENT the chart renders in
   * the explicit legacy mode (fixed `CHART_HEIGHT` + the historical
   * per-type offsets) used by `DrillSection` — the only remaining
   * drill consumer of this mode (`RowDrillPanel` passes its own
   * compact `cellPx`) — and any consumer outside the uniform grid;
   * a documented render mode, not a data fallback.
   */
  cellPx?: number;
  /** State of a single-series chart (look v3): the series takes that
   *  tone's v3 colour. Absent = the first colour of the series palette.
   *  Configs never carry a hex (the engine refuses it). */
  tone?: Tone | null;
  /** One tone per series, by position, for series that are states
   *  (MPA: NO / YES / 1P / HHO). Absent = the v3 series palette. */
  tones?: Tone[];
  /** Colours chosen by CODE (the Home AI board picks one per chart from
   *  the series palette). Never from a report config: ``chartPropsFromConfig``
   *  maps ``tone`` / ``tones`` only, and the engine refuses hex. ``tone``
   *  and ``tones`` win when both are given. */
  color?: string | null;
  colors?: string[];
  stacked?: boolean;
  // String-typed so callers can forward `props.x_format` from the
  // `dashboard_config::charts` JSONB blob without casting. The
  // internal `formatFnFor` validates and falls back to no-op for
  // unknown values.
  xFormat?: DataFormat | string | null;
  yFormat?: DataFormat | string | null;
  dataLabels?: DataLabelsConfig | null;
  axisXFormat?: AxisFormatConfig | null;
  axisYFormat?: AxisFormatConfig | null;
  axisY1Format?: AxisFormatConfig | null;
  /** Render second series on a right-side Y axis (multi_line only). */
  dualAxis?: boolean;
  tableColumns?: ReportTableColumnConfig[];
  /** Pre-resolved tooltip text for the chart title (config ``info``,
   *  localized by the caller). Rendered as an ``InfoTip`` ⓘ. */
  info?: string;
  /** Legend delta chips on/off (config ``legend_deltas``, default on).
   *  Currently consumed by ``multi_line`` (MultiLineT). */
  /** Secondary line under the title (scorecard arrangements). */
  subtitle?: string;
  /** Render `subtitle` inside the card body (opt-in — see types.ts). */
  subtitleInline?: boolean;
  /** Row density for ranked bars: `comfortable` (default) | `compact`. */
  density?: string;
  /** Share-of-total % en las barras rankeadas (default on). Apagalo
   *  cuando los valores no son partes de un todo — un indice, un
   *  precio promedio, un ratio: ahi el % del total no es ninguna
   *  cantidad del mundo. */
  showShare?: boolean;
  legendDeltas?: boolean;
  /** Tooltip "Total" row (config ``tooltip_total``, default on) —
   *  consumed by ``multi_line``. */
  tooltipTotal?: boolean;
  /** Threshold bands for the scorecard arrangement types (epic v3).
   *  Authored in the report config; nothing is inferred from a value. */
  thresholds?: Array<{ min: number | null; tone: Tone }>;
  /** Render the bands as a legend line under the chart. */
  bandsLegend?: boolean;
  /** Share-bar segments for card-grid rows (epic v3). Present =
   *  each row renders a 100%-normalized StackedShareBar from its
   *  `seg_<key>` columns instead of a single meter. Each row may
   *  additionally carry a `delta` (signed number → arrow + colour) and
   *  an optional `delta_label` (unit string, e.g. "1.0pp") to render a
   *  per-row trend chip beside the value. The row's STATE is its
   *  threshold band (ronda 4, 2026-07-21): a ToneIcon (✓/!/×) beside
   *  the % — the self-history percentile `nota` was removed by product
   *  decision ("compare against the 90/80 bands, never against
   *  yourself"). All per-row extras are DATA columns, not typed config
   *  fields; absent ⇒ nothing renders. */
  segments?: Array<{ key: string; label: DisplayLabel; tone?: Tone | null }>;
  /** Cards per row for ``metric_card_grid`` (2..4, default 4). */
  cardColumns?: number;
  /** Denominator for the meters (default 100). */
  meterMax?: number;
  /** The three words of ``stacked_share_combo`` (the share, the rest and
   *  the total line). Absent ⇒ the primitive's own scorecard wording. */
  shareComboLabels?: { good: string; bad: string; total: string };
  /** Map sub-type for `type === 'map'` — defaults to `south_america`. */
  mapVariant?: "south_america" | "chile" | string;
  /** Explicit year override for `type === 'calendar_heatmap'`. */
  calendarYear?: number | string | null;
  /** Comma-separated month/phase labels for `type === 'timeline'`. */
  timelineMonths?: string | null;
  /** Cross-filter wiring for clickable scorecards (``kpi_period_tiles``).
   *  `crossFilterKey` names the filter a tile click sets, `activeCrossValue`
   *  is that filter's current value (highlights the matching tile), and
   *  `onCrossFilter` is the host toggle. Absent ⇒ tiles are read-only. */
  crossFilterKey?: string;
  activeCrossValue?: string;
  onCrossFilter?: (filterKey: string, rawValue: string) => void;
  /** What a point's click does, in its tooltip (default: "filter"). The
   *  AI Home's drill opens the point's rows instead (2026-10-23). */
  crossFilterHint?: string;
  /** ``ranked_list`` rank slot: number (default) | up | down | none. */
  rankMarker?: string;
  /** Click on a row: the ranked_list "Ver" button, a bar_horizontal
   *  bar, a heatmap cell. The host jumps with the clicked row. With
   *  ``needsValue`` a ranked_list row without ``action_value`` gets no
   *  button. */
  rowAction?: {
    label: string;
    needsValue?: boolean;
    onClick: (row: Record<string, unknown>) => void;
  };
  /** ``ranked_list`` link under the list ("Ver los SKUs →"). */
  footerAction?: { label: string; onClick?: () => void; href?: string };
  /** In-chart controls drawn as chip groups in the card header (labels
   *  pre-resolved by the host, which owns the values). */
  controls?: Array<{
    key: string;
    label?: string;
    options: Array<{ value: string; label: string }>;
    active: string;
    onSelect: (value: string) => void;
  }>;
  /** ``multi_line``: legend entries hide / show their series. */
  legendToggle?: boolean;
  /** Replaces the generic "No data" when the SQL returns no rows. */
  emptyMessage?: string;
}

// ── Helpers ──────────────────────────────────────────────

/** Chart types whose data goes through ``toLabelValue``. */
const LABEL_VALUE_TYPES: ReadonlySet<string> = new Set([
  "bar",
  "bar_horizontal",
  "line",
  "pie",
  "funnel",
  "treemap",
  "lollipop",
  "bar_trend",
  "line_trend",
]);

function toLabelValue(
  data: Array<Record<string, unknown>>,
): Array<{ label: string; value: number }> {
  // Drop rows with missing metric — `value: 0` placeholder bars are
  // visually noisy for empty data. The empty-state guard at the
  // top of GenericChart already handles fully-empty arrays.
  return data
    .filter((d) => d.metric != null && d.metric !== "")
    .map((d) => ({
      label: String(d.dimension ?? ""),
      value: toNumber(d.metric),
    }));
}

function toGroupedShape(
  data: Array<Record<string, unknown>>,
): { rows: Array<Record<string, unknown>>; keys: string[] } {
  if (data.length === 0) return { rows: [], keys: [] };
  const firstRow = data[0]!;
  const keys = Object.keys(firstRow).filter((k) => k !== "dimension");
  const rows = data.map((d) => {
    const out: Record<string, unknown> = { label: String(d.dimension ?? "") };
    for (const k of keys) out[k] = d[k];
    return out;
  });
  return { rows, keys };
}

function formatFnFor(
  hint: DataFormat | string | null | undefined,
): ((v: number) => string) | undefined {
  if (!hint) return undefined;
  if (hint === "currency") {
    return (v) => `$${Math.round(v).toLocaleString()}`;
  }
  if (hint === "percent") {
    return (v) => `${(v * 100).toFixed(1)}%`;
  }
  if (hint === "number") {
    return (v) => v.toLocaleString();
  }
  return undefined;
}

// ── Mini-table renderer (`type: 'table'`) ────────────────

function riskScoreBadgeClass(score: number): string {
  if (score >= 70) return "bg-red-100 text-negative";
  if (score >= 40) return "bg-yellow-100 text-caution";
  return "bg-green-100 text-positive";
}

/** Per-row context for ``render: "trend"`` columns of a table chart:
 *  parsed cells + row max (micro-bar scale) + bar color from the
 *  metric (first visible) column's emoji-dot prefix. */
interface TableChartRowTrend {
  max: number;
  color: string;
  parsed: Record<string, ParsedValueDelta | null>;
}

function buildTableChartRowTrend(
  row: Record<string, unknown>,
  visibleColumns: ReportTableColumnConfig[],
  trendCols: ReportTableColumnConfig[],
  lang: Lang,
): TableChartRowTrend {
  const parsed: Record<string, ParsedValueDelta | null> = {};
  let max = 0;
  for (const c of trendCols) {
    const raw = row[c.key];
    const p = typeof raw === "string" ? parseValueDelta(raw) : null;
    parsed[c.key] = p;
    if (p && Number.isFinite(p.value)) max = Math.max(max, Math.abs(p.value));
  }
  // gating_executive's ``metrica`` column emits bilingual JSON with an
  // optional leading emoji dot — resolve locale first, then map.
  const metricCol = visibleColumns[0];
  const metricLabel = metricCol
    ? resolveLocalized(row[metricCol.key] as DisplayLabel, lang)
    : "";
  return { max, color: trendMetricColor(metricLabel), parsed };
}

function renderCell(
  value: unknown,
  column: ReportTableColumnConfig,
  lang: Lang,
  rowTrend: TableChartRowTrend | null = null,
): ReactNode {
  // Redesign — rich comparison cell (bold value + micro-bar + delta
  // chip) for columns in the ``trend`` render mode. STRICT: when the
  // cell text didn't parse, fall through to the historical rendering
  // below (incl. the ▲/▼ text coloring) — style-only degradation.
  if (column.render === "trend" && rowTrend) {
    const parsed = rowTrend.parsed[column.key] ?? null;
    if (parsed) {
      return (
        <TrendCell parsed={parsed} max={rowTrend.max} color={rowTrend.color} />
      );
    }
  }

  // Missing is not zero: a NULL/''/NaN cell falls through to the "—"
  // guard below instead of rendering a fabricated $0 / 0% / 0.0.
  const num = numOrNull(value);

  if (column.badge === "risk_score" && num !== null) {
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${riskScoreBadgeClass(num)}`}
      >
        {num.toFixed(1)}
      </span>
    );
  }

  if (
    num !== null &&
    (parseCurrencyFormat(column.format) !== null ||
      column.format === "percent" ||
      column.format === "number")
  ) {
    return <span className="tabular-nums">{formatCellValue(num, column.format)}</span>;
  }

  // Iter 16: text cells may carry a JSON-encoded bilingual
  // ``{en, es}`` string (e.g. the gating_executive pattern timeline
  // emits ``'{"en": "Stable", "es": "Estable"}'`` from SQL so the
  // user sees the right language without a separate dictionary).
  // ``resolveLocalized`` unwraps JSON-encoded bilingual strings and
  // returns plain ones verbatim — zero cost for normal text.
  if (value == null) return <span>—</span>;
  const text = resolveLocalized(value as DisplayLabel, lang) || String(value);
  // Redesign rule 4: a cell whose ENTIRE text is a bare arrow+delta
  // token ("▲ +2.0%", "▼ -9.6%", "▲ +0.5 pp") renders as the semantic
  // pastel DeltaChip. STRICT: `parseDeltaOnly` matches ONLY the bare
  // token — a "value ▲ delta" string (e.g. "164,149 ▲ +1.1%") returns
  // null and keeps the legacy coloured-text treatment below; any
  // non-delta text is untouched. Style upgrades, data never deforms.
  const deltaOnly = parseDeltaOnly(text);
  if (deltaOnly) {
    return (
      <DeltaChip
        delta={deltaOnly.dir === "up" ? 1 : -1}
        label={deltaOnly.delta}
        className="text-2xs"
      />
    );
  }
  // v1 finalization (iter 16): cells whose value carries a ▲ / ▼
  // delta marker (e.g. the gating_executive Quarterly Comparison
  // table's "164,149 ▲ +1.1%" cells) get coloured green / red
  // respectively, independent of metric semantics. The SQL already
  // emits the arrows + sign — frontend just adds the colour layer.
  if (text.includes("▲")) {
    return <span className="text-positive">{text}</span>;
  }
  if (text.includes("▼")) {
    return <span className="text-negative">{text}</span>;
  }
  return <span>{text}</span>;
}

function GenericTableChart({
  title,
  info,
  data,
  columns,
  fill = false,
}: {
  title: string;
  /** Pre-resolved tooltip text for the title (see GenericChartProps). */
  info?: string;
  data: Array<Record<string, unknown>>;
  columns: ReportTableColumnConfig[];
  /** Uniform-grid mode: the card fills its grid cell and the row zone
   *  scrolls inside it (replaces the legacy fixed `max-h-[280px]`). */
  fill?: boolean;
}) {
  const lang = useLang();
  // Row cascade entrance (coverage fix 2026-07-23): the THIRD table
  // flavor — mini-tables inside charts (fiscal comparison, cronología)
  // — must enter like DataTable and the bi-components tables do.
  // Same recipe: fade + 6px rise per row, ~20ms stagger capped at
  // 600ms total, mount-once via useEntrance (hidden/reduced/ghost
  // guards inherited).
  const entered = useEntrance();
  const reducedMotion = usePrefersReducedMotion();
  const rowEntranceStyle = (i: number): CSSProperties | undefined => {
    if (reducedMotion) return undefined;
    return {
      opacity: entered ? 1 : 0,
      transform: entered ? "none" : "translateY(6px)",
      transition: `opacity ${MOTION.durBase}ms ${MOTION.easeOut} ${Math.min(
        i * 20,
        600 - MOTION.durBase,
      )}ms, transform ${MOTION.durBase}ms ${MOTION.easeOut} ${Math.min(
        i * 20,
        600 - MOTION.durBase,
      )}ms, background-color 150ms ${MOTION.easeOut}, box-shadow 150ms ${MOTION.easeOut}`,
    };
  };
  // Convention: a column keyed "Descripción" / "Description" / "_tooltip"
  // is treated as ROW-LEVEL tooltip — its value renders as the row's
  // ``title`` attribute on hover rather than as a visible column. Keeps
  // the cronología table compact while preserving the legacy
  // per-pattern explanation for users that want it.
  const tooltipKeys = new Set(["Descripción", "Description", "_tooltip"]);
  const visibleColumns = columns.filter((c) => !tooltipKeys.has(c.key));
  const tooltipKey =
    columns.find((c) => tooltipKeys.has(c.key))?.key ?? null;
  // Redesign — columns in the ``trend`` render mode (rich comparison
  // cells). Their header + cells right-align, like the mockup.
  const trendCols = visibleColumns.filter((c) => c.render === "trend");
  return (
    // Legacy (non-grid) mode: the max-h matches the ``CHART_HEIGHT``
    // constant below (used by bar/bubble/scatter renderers) so a table
    // chart sitting next to them in the same row stays the same height.
    // Grid (`fill`) mode: the card stretches to its cell and the row
    // zone claims the leftover space (`flex-1 min-h-0`) — the cell
    // bounds the height, no px constant involved.
    <div data-table-surface="" data-density="compact"
      className={`rounded-[12px] border border-neutral-200 bg-white p-4 shadow-card${
        fill ? " h-full flex flex-col overflow-hidden" : ""
      }`}
    >
      <div className="mb-2 flex shrink-0 items-center gap-1.5">
        <h3 className="text-neutral-800 font-semibold">{title}</h3>
        {info && <InfoTip text={info} className="text-neutral-400" />}
      </div>
      <div
        className={
          fill
            ? "flex-1 min-h-0 overflow-y-auto overflow-x-auto"
            : "max-h-[280px] overflow-y-auto overflow-x-auto"
        }
      >
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-neutral-50">
            <tr className="text-2xs font-semibold text-neutral-500">
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  className={`${
                    c.render === "trend" ? "text-right" : "text-left"
                  } py-2 pr-2 first:pl-2 border-b border-neutral-200`}
                  // Trend cells reserve a fixed 66px chip zone (+5px gap)
                  // to the right of the value+bar stack, so the header
                  // needs the same offset for its right edge to sit on
                  // the VALUE column (8px td pr-2 + 71px zone).
                  style={c.render === "trend" ? { paddingRight: 79 } : undefined}
                >
                  {/* iter 19 — column ``label`` may be bilingual. */}
                  {resolveLocalized(c.label as DisplayLabel, lang)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              // Pattern Timeline tooltip (and any ``_tooltip`` /
              // ``Description`` column) can come from a SQL CASE that
              // emits a literal ``{"en": ..., "es": ...}`` string. Pass
              // through ``resolveLocalized`` so the JSON shape collapses
              // to the active locale before the browser displays it as
              // the native title attribute.
              const tooltipRaw =
                tooltipKey != null ? row[tooltipKey] : undefined;
              const tooltip = resolveLocalized(
                tooltipRaw as DisplayLabel,
                lang,
              );
              const rowTrend =
                trendCols.length > 0
                  ? buildTableChartRowTrend(row, visibleColumns, trendCols, lang)
                  : null;
              return (
                <tr
                  key={i}
                  title={tooltip || undefined}
                  style={rowEntranceStyle(i)}
                  className={`${
                    i % 2 === 0 ? "bg-white" : "bg-neutral-50/50"
                  } cursor-pointer hover:bg-primary-soft hover:shadow-[inset_3px_0_0_0_var(--omni-violet)]`}
                >
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={`${
                        // Trend cells (value + micro-bar) get a touch
                        // more vertical padding; alignment untouched.
                        c.render === "trend" ? "py-1.5" : "py-1"
                      } pr-2 text-neutral-700 ${
                        c.render === "trend" ? "text-right align-top" : ""
                      }`}
                    >
                      {renderCell(row[c.key], c, lang, rowTrend)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main export ──────────────────────────────────────────

/** Legacy fixed SVG height — the explicit non-grid render mode (drill
 *  panels + consumers that don't pass `cellPx`). */
const CHART_HEIGHT = 280;

// Card-chrome constants (CHART_CARD_CHROME_PX = 62, MAP_CARD_CHROME_PX
// = 47) live in `@pervasivemind/bi-components` NEXT TO the markup they
// measure (`shared.jsx::ChartCard` / `maps.jsx`) — review #12: a
// padding/typography change and its measured constant travel in the
// same package. This module only consumes them:
// `cellPx − CHART_CARD_CHROME_PX` = the SVG plot height that makes the
// card land EXACTLY on its grid cell.

/** Defensive floor for the derived plot height — a cell too small for
 *  any legible plot (config below the type's sensible MIN_H) still
 *  renders instead of producing a negative SVG height. Render defense
 *  only; `validate_config` bounds `height` at publish time. */
const MIN_PLOT_PX = 80;

/** Column tracks of a `metric_card_grid` card with share-bar rows:
 *  label (as wide as the card's longest label, each capped at 104px) ·
 *  bar (the rest) · band icon + % (when ANY row of the card has a value)
 *  · delta (when any row has one). Every row is a `subgrid` of these
 *  tracks (`SHARE_ROW_STYLE`), which is what makes the bars start and
 *  end on the same x across the card. A track no row uses is left out
 *  rather than kept empty, so the bars do not lose a column gap to it. */
function shareRowLayout(hasValue: boolean, hasDelta: boolean): {
  columns: string;
  deltaCol: number;
} {
  const tracks = ["minmax(0, max-content)", "minmax(0, 1fr)"];
  if (hasValue) tracks.push("auto", "auto");
  if (hasDelta) tracks.push("auto");
  return { columns: tracks.join(" "), deltaCol: hasValue ? 5 : 3 };
}
const SHARE_ROW_STYLE = { gridColumn: "1 / -1", gridTemplateColumns: "subgrid" } as const;

/** Presence-preserving numeric read for the scorecard types.
 *
 *  `toNumber` deliberately coerces absent/NaN to 0 so SVG geometry
 *  never goes NaN — correct for plot coordinates, WRONG here: a
 *  missing score and a score of zero are different facts, and the
 *  scorecard primitives were built to show that difference (an empty
 *  track with an em-dash, not a zero-length bar next to "0"). Using
 *  `toNumber` for presence silently fabricates data. Also used by
 *  `multi_line`: an explicit NULL point is a gap, not a zero. */
function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The authored bands, rendered as a legend line.
 *
 *  Generated from the SAME array that tints the meters, so the legend
 *  and the colours are structurally incapable of disagreeing — the
 *  mockup's "Thresholds: Green >=95 / Yellow 85-94 / Red <85" typed a
 *  second time would drift the first time anyone recalibrates. */
function BandsLegend({
  bands,
}: {
  bands: Array<{ min: number | null; tone: Tone }>;
}): JSX.Element | null {
  if (!bands || bands.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-600">
      {bands.map((band, i) => {
        const upper = i === 0 ? null : bands[i - 1]!.min;
        const label =
          upper == null
            ? `≥ ${band.min}`
            : Number.isFinite(band.min)
              ? `${band.min}–${upper}`
              : `< ${upper}`;
        return (
          <span key={`${band.tone}-${i}`} className="inline-flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: toneStyle(band.tone).fill }}
              aria-hidden="true"
            />
            {label}
          </span>
        );
      })}
    </div>
  );
}

/** A row column that names a tone (``tone``, ``dot_tone``). Anything
 *  else paints nothing: the engine refuses hex colours in the SQL. */
function isConfigTone(value: unknown): value is Tone {
  return typeof value === "string" && (TONES as readonly string[]).includes(value);
}

export function GenericChart({
  type,
  title,
  data,
  error,
  cellPx,
  tone,
  tones,
  color,
  colors,
  stacked,
  xFormat,
  yFormat,
  dataLabels,
  axisXFormat: _axisXFormat,
  axisYFormat,
  axisY1Format,
  dualAxis,
  tableColumns,
  info,
  subtitle,
  subtitleInline = false,
  density,
  showShare = true,
  legendDeltas = true,
  tooltipTotal = true,
  thresholds,
  bandsLegend = false,
  segments: authoredSegments,
  cardColumns,
  meterMax,
  shareComboLabels,
  mapVariant,
  calendarYear,
  timelineMonths,
  crossFilterKey,
  activeCrossValue,
  onCrossFilter,
  crossFilterHint,
  rankMarker,
  rowAction,
  footerAction,
  controls,
  legendToggle = false,
  emptyMessage,
}: GenericChartProps) {
  const clientTheme = useColorTheme();
  // A segment that names a state takes that tone's v3 colour; one that
  // is a category (a brand in Share of Search) takes the next colour of
  // the series palette, counting only the categories.
  let categoryIndex = 0;
  const segments = authoredSegments?.map((seg) => ({
    ...seg,
    color: seg.tone
      ? toneStyle(seg.tone).fill
      : clientTheme.palette[categoryIndex++ % clientTheme.palette.length]!,
  }));
  void _axisXFormat;
  // Active i18n locale — used by ``diverging_stacked_bar`` for legend
  // / tooltip prefixes (``Inflow YES (+)`` in EN, ``Entrada YES (+)``
  // in ES). Top-level so the hook order stays stable across the early
  // returns below.
  const chartLang = useLang();
  const { t } = useTranslation();
  // Entrance + refetch language for the marks THIS file draws BY HAND
  // (r16 parity — the audit found them still speaking the r12 dialect).
  //
  // `useGrowCycle` is the SAME driver the 29 chart call sites feed to
  // `EntranceGroup`'s controlled mode, so a hand-drawn mark now says
  // exactly what the charts beside it say:
  //   - first mount → false → (zero paints → idle → `delayEntrance`
  //     beat) → true. Byte-identical to the `useEntrance()` it replaces:
  //     the approved first load does not move.
  //   - data change → false for ONE painted frame WITHOUT transition,
  //     then true → the mark RE-GROWS to its new value, in the same wave
  //     as the numbers that morph (NumberTween) and the sibling charts
  //     that re-grow. What grows is the NEW value; no intermediate of
  //     the old one is ever shown as if it were the datum.
  // Reduced motion, hidden document and FadeSwap ghosts render settled —
  // the hook owns those guards plus the timeout backstop (Chrome pauses
  // rAF without notice), so the reset can never strand at zero.
  //
  // `kpi_period_tiles` (the CHS period rings) is the ONLY type in this
  // module with a hand-drawn animatable mark; every other block's bars
  // are bi-components primitives that own their own motion. So the
  // redraw key is computed for that type ONLY — the rest hash NOTHING
  // (constant key ⇒ no pulse, no work). The hash is memoized on the
  // `data` IDENTITY because this component re-renders on every tooltip
  // mousemove; re-hashing the payload per render is precisely the cost
  // the chart call sites avoid with the same `useMemo`.
  const drawsRings = type === "kpi_period_tiles";
  const marksSig = useMemo(
    () => (drawsRings ? lineDataSig(data) : 0),
    [drawsRings, data],
  );
  const marksDrawn = useGrowCycle(marksSig);
  const reducedMotion = usePrefersReducedMotion();

  // Uniform-grid mode: derive the SVG plot height from the cell. The
  // legacy per-type offsets (+20/+40/+60/+100/+200/−40) are GONE here —
  // that knowledge now lives as per-type DEFAULT_H row units in
  // ``grid-layout.ts`` (mirrored in the engine's config_compat).
  const fill = cellPx != null;
  const plotH = fill ? Math.max(MIN_PLOT_PX, cellPx - CHART_CARD_CHROME_PX) : null;

  // Error / no-data states render through the shared MessageCard
  // (review #12) — one card, ChartCard-aligned chrome, instead of the
  // hand-rolled per-site divs that drifted (p-4 vs px-5).
  if (error) {
    return (
      <MessageCard title={title} fill={fill} tone="error">
        {error}
      </MessageCard>
    );
  }

  // ── Chrome-less tinted surfaces (epic v3) ────────────────────
  //
  // These render BEFORE the no-data guard and OUTSIDE the ChartCard
  // wrapper, for two reasons that are easy to lose:
  //
  //   * A tinted banner inside a white bordered card with an empty
  //     header row is not the design — the card IS the chrome here.
  //   * The grid arithmetic has to stay honest. Everything the switch
  //     returns gets `cellPx − CHART_CARD_CHROME_PX` (62) as its plot
  //     budget against an 80px floor, so an h=2 banner (96px cell)
  //     would overflow its own cell. These declare zero chrome.
  //
  // Zero rows is an ERROR, not an empty state: an authored banner with
  // no row is a config bug, and rendering "No data" would make it look
  // like a legitimately empty dataset.
  if (type === "callout" || type === "meta_grid") {
    if (data.length === 0) {
      return (
        <MessageCard title={title} fill={fill} tone="error">
          {t("dashboard.chart.calloutNoRow", { defaultValue: "This block returned no row" })}
        </MessageCard>
      );
    }
    const head = (data[0] ?? {}) as Record<string, unknown>;
    if (type === "callout") {
      return (
        <Callout
          tone={(head.tone as Tone) ?? "neutral"}
          accent={head.accent === "info" ? "info" : undefined}
          title={head.title != null ? String(head.title) : undefined}
          role={head.role === "alert" ? "alert" : "note"}
          aiGenerated={head.ai_generated === true}
          aiLabel={t("dashboard.chart.aiGenerated", { defaultValue: "AI" })}
          dense={head.dense === true}
          className={fill ? "h-full" : undefined}
        >
          {String(head.text ?? "")}
        </Callout>
      );
    }
    return (
      <MetaGrid
        items={(data as Array<Record<string, unknown>>).map((row) => ({
          label: String(row.label ?? ""),
          value: String(row.value ?? ""),
        }))}
        columns={
          cardColumns === 1 || cardColumns === 3 ? cardColumns : 2
        }
        className={fill ? "h-full" : undefined}
      />
    );
  }

  if (data.length === 0) {
    return (
      <MessageCard title={title} fill={fill}>
        {emptyMessage || t("common.noData")}
      </MessageCard>
    );
  }

  // Label/value charts read ``dimension`` + ``metric``. Rows that carry
  // neither ``metric`` key (e.g. a SQL aliased ``AS value``) used to be
  // filtered to nothing and showed "No data" over a non-empty result
  // (QA D-UIB-2). Say what is wrong instead.
  if (
    LABEL_VALUE_TYPES.has(type) &&
    data.every((row) => !Object.prototype.hasOwnProperty.call(row, "metric"))
  ) {
    return (
      <MessageCard title={title} fill={fill} tone="error">
        {t("dashboard.chart.missingMetric", {
          columns: Object.keys(data[0] ?? {}).join(", "),
        })}
      </MessageCard>
    );
  }

  if (type === "table") {
    return (
      <GenericTableChart
        title={title}
        info={info}
        data={data}
        columns={tableColumns ?? []}
        fill={fill}
      />
    );
  }

  const palette =
    tones && tones.length > 0
      ? tones.map((t) => toneStyle(t).fill)
      : colors && colors.length > 0
        ? colors
        : clientTheme.palette;
  const primary = tone ? toneStyle(tone).fill : (color ?? palette[0]!);
  const yFmt = formatFnFor(yFormat);
  const xFmt = formatFnFor(xFormat);

  let body: ReactNode;

  switch (type) {
    case "bar": {
      // ``rowAction`` hands the host the ORIGINAL row of the clicked bar
      // (the rows ``toLabelValue`` keeps, in its order), as bar_horizontal.
      const barRows = data.filter((d) => d.metric != null && d.metric !== "");
      body = (
        <VerticalBarT
          data={toLabelValue(data)}
          onBarClick={rowAction ? (_label: string, i: number) => rowAction.onClick(barRows[i]!) : undefined}
          clickHint={rowAction?.label}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          format={yFmt}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    // ── Scorecard arrangements (epic v3) ─────────────────────
    //
    // Both consume a FLAT one-row-per-leaf payload from `raw_sql`, and
    // the ENGINE refuses a SQL that aliases `metric`/`dimension` — that
    // constraint is what makes a config reaching older code render an
    // empty card instead of a plausible-but-wrong bar chart.
    //
    // Neither caps rows: the card scrolls. Real coverage is 17
    // marketplaces spread unevenly across 8 countries, not the mockup's
    // tidy four-per-card, so a fixed N would silently drop data. Any
    // top-N is an authored SQL LIMIT, visible in the card subtitle.
    case "metric_strip": {
      // A titled BAND of 1..4 blocks — the "Regional Health" shape.
      // Chrome-less like callout/meta_grid would be wrong here: the
      // band IS a card, it just carries its title inside instead of in
      // a separate header.
      const rows = data as Array<Record<string, unknown>>;
      const spec = thresholds?.length ? { thresholds } : undefined;
      return (
        <MetricStrip
          title={typeof title === "string" ? title : undefined}
          subtitle={subtitle}
          max={meterMax ?? 100}
          note={
            bandsLegend && thresholds?.length
              ? thresholds
                  .map((band, i) => {
                    const upper = i === 0 ? null : thresholds[i - 1]!.min;
                    return upper == null
                      ? `≥ ${band.min}`
                      : band.min == null
                        ? `< ${upper}`
                        : `${band.min}–${upper}`;
                  })
                  .join(" · ")
              : undefined
          }
          items={rows.map((row) => ({
            label: String(row.label ?? ""),
            value: numOrNull(row.value),
            valueLabel: row.value_label != null ? String(row.value_label) : undefined,
            delta: row.delta != null ? String(row.delta) : undefined,
            deltaTone: (row.delta_tone as Tone) ?? undefined,
            toneSpec: spec,
            toneLabel: row.tone_label != null ? String(row.tone_label) : undefined,
          }))}
          className={fill ? "h-full" : undefined}
        />
      );
    }
    case "kpi_period_tiles": {
      // A single row of period tiles that BOTH compare and select: each
      // row is `{period_key, label, value}`, coloured by the bands; the
      // tile whose `period_key` equals the active filter value is
      // highlighted; clicking a tile SETS that filter (cross-filter).
      // The row of tiles IS the period control — there is no separate
      // filter widget.
      const rows = data as Array<Record<string, unknown>>;
      const spec = thresholds?.length ? { thresholds } : undefined;
      const clickable = !!(crossFilterKey && onCrossFilter);
      return (
        <div
          className="flex flex-wrap gap-2"
          style={fill ? { height: "100%", alignContent: "flex-start" } : undefined}
          role={clickable ? "radiogroup" : undefined}
          aria-label={typeof title === "string" ? title : "Periods"}
        >
          {rows.map((row, i) => {
            const v = numOrNull(row.value);
            const periodKey = String(row.period_key ?? row.label ?? i);
            const active = activeCrossValue != null && periodKey === activeCrossValue;
            const tone = spec ? resolveTone(spec, v) : "neutral";
            const palette = toneStyle(tone);
            // KPIRing-style tile (2026-07 CHS feedback): progress ring
            // coloured by the band + label/value column — same geometry
            // as bi-components' KPIRing (r=24, sw=5), rendered inline so
            // the button chrome owns hover/active. The value colour comes
            // from the band (absolute health); the ACTIVE ring is the
            // selection affordance, kept separate from the tone so
            // "which period" never reads as "how good".
            const ringR = 24;
            const ringSw = 5;
            const circ = 2 * Math.PI * ringR;
            const pct = v === null ? 0 : Math.max(0, Math.min(1, v));
            const dashOffset = circ - pct * circ;
            const valueLabel =
              v === null
                ? "—"
                : row.value_label != null
                  ? String(row.value_label)
                  : formatValue(v, "percent", 1);
            // Ronda 4: the score IS the band (90/80 thresholds — never a
            // self-history percentile, product decision 2026-07-21). The
            // % lives INSIDE the ring; the BIG element beside is the
            // band's ToneIcon (✓/!/×) — a SHAPE, so the state survives
            // red-green colour-blindness.
            const tile = (
              <>
                <div className="relative shrink-0">
                  <svg
                    width="58"
                    height="58"
                    className="-rotate-90 transform"
                    role="img"
                    aria-label={valueLabel}
                  >
                    <circle cx="29" cy="29" r={ringR} fill="none" className="stroke-neutral-100" strokeWidth={ringSw} />
                    {v !== null && (
                      <circle
                        cx="29"
                        cy="29"
                        r={ringR}
                        fill="none"
                        stroke={palette.fg}
                        strokeWidth={ringSw}
                        strokeDasharray={circ}
                        // Grow cycle (see `marksDrawn` at the top): the
                        // sweep plays on the first paint AND again when
                        // the block's data changes, so filtering makes
                        // the ring re-grow with its siblings instead of
                        // sliding on its own. The transition is attached
                        // only on the `drawn` frame — during the one
                        // zero frame it must be absent or the stroke
                        // would animate BACKWARDS before redrawing
                        // (same rule as `EntranceGroup` controlled and
                        // `useLineDraw`).
                        strokeDashoffset={marksDrawn ? dashOffset : circ}
                        strokeLinecap="round"
                        style={{
                          transition:
                            reducedMotion || !marksDrawn
                              ? undefined
                              : `stroke-dashoffset ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
                        }}
                      />
                    )}
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-bold tabular-nums text-neutral-800">
                    {v === null ? (
                      "—"
                    ) : (
                      <NumberTween
                        fromZero
                        value={pct * 100}
                        durationMs={MOTION.durEntrance}
                        delayMs={MOTION.delayEntrance}
                        format={(nn) => `${Math.round(nn)}%`}
                      />
                    )}
                  </span>
                </div>
                <div className="min-w-0">
                  {/* Ronda 11: the per-tile ⓘ sits at the tile's TOP-RIGHT
                      corner (not inline with the label) and rests at
                      near-full opacity ("demasiado tenue") — a shade
                      darker but still chrome, never data. InfoTip
                      portals + stops propagation, so it is safe inside
                      the tile. Data column `info`; absent ⇒ nothing. */}
                  {row.info != null && String(row.info) !== "" && (
                    <span className="absolute right-2 top-2">
                      <InfoTip strong text={String(row.info)} className="text-neutral-500" />
                    </span>
                  )}
                  <span className="block truncate text-[11px] font-semibold uppercase tracking-wider text-neutral-600">
                    {String(row.label ?? "")}
                  </span>
                  <span className="mt-1 flex items-center gap-2">
                    <ToneIcon tone={tone} size={20} ariaLabel={`${String(row.label ?? "")}: ${tone}`} className="shrink-0" />
                    {/* Ronda 5: listing COUNTS (authorized vs no) give
                        the tile substance beyond the ratio — data
                        columns n_auth / n_no; absent ⇒ nothing. */}
                    {/* Counts WRAP, they never truncate. Measured on
                        staging 2026-08-04: at a 632px board tile
                        "22.6K auth · 2.1K no" needed 101px and had 78px,
                        so the reader lost the second number entirely. A
                        count cut in half is not a smaller count, it is a
                        different one — the line grows instead. */}
                    {(numOrNull(row.n_auth) !== null || numOrNull(row.n_no) !== null) && (
                      <span className="min-w-0 text-[11px] tabular-nums leading-tight">
                        <span className="font-semibold text-neutral-700">
                          {numOrNull(row.n_auth) === null ? "—" : (
                            <NumberTween
                              fromZero
                              durationMs={MOTION.durEntrance}
                              delayMs={MOTION.delayEntrance}
                              value={numOrNull(row.n_auth) as number}
                              format={fmtN}
                            />
                          )}
                        </span>
                        <span className="text-neutral-600"> auth · </span>
                        <span className="font-semibold text-neutral-700">
                          {numOrNull(row.n_no) === null ? "—" : (
                            <NumberTween
                              fromZero
                              durationMs={MOTION.durEntrance}
                              delayMs={MOTION.delayEntrance}
                              value={numOrNull(row.n_no) as number}
                              format={fmtN}
                            />
                          )}
                        </span>
                        <span className="text-neutral-600"> no</span>
                      </span>
                    )}
                  </span>
                </div>
              </>
            );
            const base =
              "relative min-w-[168px] flex-1 rounded-[14px] border px-4 py-3 text-left transition-all flex items-center gap-3 shadow-card";
            const activeCls = active
              ? "border-accent bg-accent/10 ring-2 ring-accent/40"
              : "border-neutral-200 bg-white hover:shadow-lg motion-safe:hover:-translate-y-0.5";
            return clickable ? (
              <button
                key={periodKey}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onCrossFilter!(crossFilterKey!, periodKey)}
                className={`${base} ${activeCls}`}
              >
                {tile}
              </button>
            ) : (
              <div key={periodKey} className={`${base} ${active ? "border-accent bg-accent/10" : "border-neutral-200 bg-white"}`}>
                {tile}
              </div>
            );
          })}
        </div>
      );
    }
    case "stacked_share_combo": {
      // CHS retailer drill (ronda 6): weekly 100%-stacked mix columns +
      // right-axis total line. Rows are `{label, value, total}` with
      // `value` the authorized FRACTION. The dashed target comes from
      // the INHERITED threshold bands — the min of the "good" band
      // (0.9 today) — so when the bands change centrally, the target
      // line moves with them instead of living hardcoded in a chart.
      const rows = data as Array<Record<string, unknown>>;
      const goodBand = thresholds?.find(
        (b) => b.tone === "good" && typeof b.min === "number",
      );
      body = (
        <StackedShareComboT
          data={rows.map((row) => ({
            label: String(row.label ?? ""),
            value: numOrNull(row.value),
            // null stays null — the primitive BREAKS the line there
            // instead of drawing a fabricated zero.
            total: numOrNull(row.total),
          }))}
          target={goodBand ? (goodBand.min as number) : null}
          height={plotH ?? CHART_HEIGHT}
          {...(shareComboLabels
            ? {
                goodLabel: shareComboLabels.good,
                badLabel: shareComboLabels.bad,
                totalLabel: shareComboLabels.total,
              }
            : {})}
        />
      );
      break;
    }
    case "ranked_list": {
      // Rank + title + subtitle + severity chip. The mockup's "Top
      // Global Risks" is a LIST, not a bar chart: the magnitude is
      // carried by the rank order and the chip, and forcing it into
      // meters (as the first cut did) buried the ranking under ten
      // near-identical red bars.
      const rows = data as Array<Record<string, unknown>>;
      return (
        <div
          className="rounded-[14px] border border-neutral-200 bg-white px-5 py-4 shadow-card"
          style={fill ? { height: "100%", display: "flex", flexDirection: "column" } : undefined}
        >
          {typeof title === "string" && title && (
            <div className="mb-1 flex items-center gap-1.5">
              <h3 className="truncate text-sm font-bold text-neutral-800">{title}</h3>
              {info && <InfoTip text={info} className="text-neutral-400" />}
            </div>
          )}
          {subtitle && <p className="mb-2 truncate text-xs text-neutral-600">{subtitle}</p>}
          <div className="min-h-0 flex-1">
            <RankedList
              // label / note / chip may be SQL-built bilingual JSON
              // (`{"en": …, "es": …}`), the same contract table cells have.
              items={rows.map((row, i) => ({
                // A label is not an identity: two rows can read "Kawai"
                // (QA F06, duplicate React keys). Without an `id` column
                // the row's position joins the label.
                id: row.id != null ? String(row.id) : `${i}:${String(row.label ?? "")}`,
                rank: numOrNull(row.rank) ?? undefined,
                title: resolveLocalized((row.label ?? "") as DisplayLabel, chartLang),
                subtitle:
                  row.note != null
                    ? resolveLocalized(row.note as DisplayLabel, chartLang)
                    : undefined,
                chip:
                  row.chip != null
                    ? {
                        label: resolveLocalized(row.chip as DisplayLabel, chartLang),
                        tone: (row.chip_tone as Tone) ?? "neutral",
                      }
                    : undefined,
                // DSA prototype v7 extras — each one only when its
                // column is present, so older lists render unchanged.
                // The row names the dot's STATE (`dot_tone`); the component
                // paints it. A category (a retailer) carries no dot.
                dot: isConfigTone(row.dot_tone) ? toneStyle(row.dot_tone).fill : undefined,
                value:
                  row.value != null && row.value !== ""
                    ? resolveLocalized(row.value as DisplayLabel, chartLang)
                    : undefined,
                valueTone: (row.value_tone as Tone) ?? undefined,
                valueToneLabel:
                  row.value_tone_label != null
                    ? resolveLocalized(row.value_tone_label as DisplayLabel, chartLang)
                    : undefined,
                action:
                  rowAction &&
                  (!rowAction.needsValue || (row.action_value != null && row.action_value !== ""))
                    ? { label: rowAction.label, onClick: () => rowAction.onClick(row) }
                    : undefined,
              }))}
              marker={
                rankMarker === "up" || rankMarker === "down" || rankMarker === "none"
                  ? rankMarker
                  : "number"
              }
              maxHeight={plotH ?? undefined}
              ariaLabel={typeof title === "string" ? title : "Ranked list"}
            />
          </div>
          {footerAction && (
            <div className="mt-2 shrink-0">
              {footerAction.onClick || !footerAction.href ? (
                <button
                  type="button"
                  onClick={footerAction.onClick}
                  disabled={!footerAction.onClick}
                  className="rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
                >
                  {footerAction.label}
                </button>
              ) : (
                <a
                  href={footerAction.href}
                  className="inline-block rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  {footerAction.label}
                </a>
              )}
            </div>
          )}
        </div>
      );
    }
    case "metric_rows": {
      const rows = data as Array<Record<string, unknown>>;
      const spec = thresholds?.length ? { thresholds } : undefined;
      body = (
        <div
          className="flex flex-col gap-3 overflow-y-auto pr-1"
          style={plotH ? { maxHeight: plotH } : undefined}
        >
          {rows.map((row, i) => {
            const value = numOrNull(row.value);
            return (
              <div key={String(row.label ?? i)} className="space-y-1">
                <MeterBar
                  label={String(row.label ?? "")}
                  labelWidth={140}
                  value={value}
                  max={meterMax ?? 100}
                  toneSpec={spec}
                  showValue
                  // A host that formats its own values (a 0..1 share read
                  // as "92,4 %") sends the text; absent, the raw number.
                  valueLabel={row.value_label != null ? String(row.value_label) : undefined}
                  ariaLabel={String(row.label ?? "")}
                  toneLabel={row.tone_label ? String(row.tone_label) : undefined}
                />
                {row.note != null && String(row.note) !== "" && (
                  <p className="pl-[148px] text-[11px] text-neutral-600">
                    {String(row.note)}
                  </p>
                )}
              </div>
            );
          })}
          {bandsLegend && thresholds?.length ? (
            <BandsLegend bands={thresholds} />
          ) : null}
        </div>
      );
      break;
    }
    case "metric_card_grid": {
      const rows = data as Array<Record<string, unknown>>;
      const spec = thresholds?.length ? { thresholds } : undefined;
      // Group by the `group` column, preserving first-seen order — the
      // SQL's ORDER BY is the author's intent and must survive.
      const order: string[] = [];
      const grouped = new Map<string, Array<Record<string, unknown>>>();
      for (const row of rows) {
        const key = String(row.group ?? "");
        if (!grouped.has(key)) {
          grouped.set(key, []);
          order.push(key);
        }
        grouped.get(key)!.push(row);
      }
      // Authored count is a CEILING, not an absolute: the same block
      // renders in a dashboard column AND in a board tile the user can
      // drag to any width. ResponsiveCardGrid drops a column rather
      // than squeezing a card below what its labels need.
      const authoredCols = cardColumns ?? 4;
      // Reserve vertical room for up to N rows (default 6 — the
      // operator's number). Cards with fewer rows keep the space so the
      // grid reads as a table of cards rather than a ragged stack.
      const rowSlots = Math.min(
        6,
        Math.max(1, ...order.map((k) => (grouped.get(k) ?? []).length)),
      );
      const shareRows = !!(segments && segments.length > 0);
      body = (
        <div
          className="overflow-y-auto pr-1"
          style={plotH ? { maxHeight: plotH } : undefined}
        >
          {/* Segment legend. Without it the stacked bars are four
              unlabelled colours: the reader can see that a row is
              mostly orange and has no way to learn that orange means
              "no HP agreement". The colours come from the SAME
              authored declaration that paints the bars, so the legend
              cannot drift from them. */}
          {segments && segments.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              {segments.map((seg) => (
                <span
                  key={seg.key}
                  className="inline-flex items-center gap-1.5 text-[11px] text-neutral-500"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: seg.color }}
                    aria-hidden="true"
                  />
                  {resolveLocalized(seg.label, chartLang)}
                </span>
              ))}
            </div>
          )}
          <ResponsiveCardGrid maxColumns={authoredCols} gapPx={12}>
            {order.map((key) => {
              const groupRows = grouped.get(key) ?? [];
              const head = groupRows[0] ?? {};
              const score = numOrNull(head.group_score);
              const hasScore = score !== null;
              const shareLayout = shareRowLayout(
                groupRows.some((r) => numOrNull(r.value) !== null),
                groupRows.some((r) => numOrNull(r.delta) !== null),
              );
              return (
                <div
                  key={key}
                  className="flex h-full flex-col gap-2.5 rounded-[14px] border border-neutral-200 bg-white px-4 py-3 shadow-card"
                >
                  {/* Hero (ronda 3 redesign): country name left; right
                      side leads with the INTEGER 1–5 nota (bold, dark)
                      and the band-coloured % beside it — the tiny chip
                      died ("super mal lograda"). The meter below gets a
                      hover tooltip with the same reading. */}
                  <div className="flex items-end justify-between gap-2">
                    <div className="min-w-0">
                      {head.group_eyebrow != null && (
                        <p className="truncate text-2xs uppercase tracking-wide text-neutral-600">
                          {String(head.group_eyebrow)}
                        </p>
                      )}
                      <p className="truncate text-base font-bold text-neutral-800">
                        {key}
                      </p>
                    </div>
                    {hasScore && (
                      <div className="flex shrink-0 items-center gap-2.5">
                        {head.group_delta != null && (
                          <span className="text-2xs text-neutral-600">
                            {String(head.group_delta)}
                          </span>
                        )}
                        <span
                          className="text-lg font-extrabold tabular-nums leading-none"
                          style={{
                            color: spec
                              ? toneStyle(resolveTone(spec, score)).fg
                              : undefined,
                          }}
                        >
                          {/* Ronda 15: the big country % counts from
                              zero. The label is an SQL-formatted
                              string — parseFormattedNumber counts only
                              when unambiguous and lands the ORIGINAL
                              string verbatim; otherwise static. */}
                          {(() => {
                            const raw = String(head.group_score_label ?? score);
                            const parsed = parseFormattedNumber(raw);
                            return parsed ? (
                              <NumberTween
                                fromZero
                                durationMs={MOTION.durEntrance}
                                delayMs={MOTION.delayEntrance}
                                value={parsed.value}
                                format={parsed.render}
                              />
                            ) : (
                              raw
                            );
                          })()}
                        </span>
                        {/* Ronda 4: band state as a SHAPE (✓/!/×) — the
                            self-history percentile died; the score IS
                            the 90/80 band. */}
                        <ToneIcon
                          tone={spec ? resolveTone(spec, score) : "neutral"}
                          size={18}
                          ariaLabel={`${key}: ${spec ? resolveTone(spec, score) : "neutral"}`}
                        />
                      </div>
                    )}
                  </div>
                  {hasScore && (
                    <MeterBar
                      value={score}
                      max={meterMax ?? 100}
                      toneSpec={spec}
                      height={10}
                      ariaLabel={`${key} overall`}
                      hoverText={`${key} · ${String(head.group_score_label ?? score)}`}
                    />
                  )}
                  {/* The rows zone owns the leftover height and
                      reserves room for `rowSlots` rows, so cards with
                      fewer marketplaces do not collapse and the note
                      below stays on one baseline across the grid. A top
                      hairline + breathing room separates the country
                      meter from the retailer rows (ronda 4 feedback). */}
                  {/* Share-bar rows share ONE column grid per card
                      (each row is a `subgrid` of it): label · bar ·
                      band icon · % · delta. The label track is as wide
                      as the card's longest label (still capped at
                      104px), so every bar STARTS on the same x; the
                      trailing tracks keep their width on a row with no
                      value or no delta, so every bar ENDS on the same
                      x too. Before, each row was its own flex line: a
                      short label pulled its bar left and a row without
                      a value stretched its bar to the card edge.
                      `content-start` keeps the reserved `rowSlots`
                      height from stretching the rows apart. */}
                  <div
                    className={`mt-0.5 border-t border-neutral-100 pt-2.5 ${
                      shareRows
                        ? "grid content-start gap-x-2.5 gap-y-2.5"
                        : "flex flex-col gap-2.5"
                    }`}
                    style={{
                      minHeight: rowSlots * 22,
                      ...(shareRows ? { gridTemplateColumns: shareLayout.columns } : {}),
                    }}
                    data-share-rows={shareRows ? "" : undefined}
                  >
                    {groupRows.map((row, i) => {
                      const v = numOrNull(row.value);
                      const rowLabel = String(row.label ?? "");
                      // Retailer drill (ronda 6): when the card grid
                      // declares a chart-level `cross_filter_key`, each
                      // row becomes a BUTTON — clicking selects the
                      // retailer (`"COUNTRY|MARKETPLACE"`) and any
                      // sibling chart reading `{filter:KEY}` re-windows
                      // to it. Selected row = accent ring (selection
                      // affordance, same vocabulary as the period
                      // tiles); second click deselects (back to the
                      // config default).
                      const rowClickable = !!(crossFilterKey && onCrossFilter);
                      const rowValue = `${key}|${rowLabel}`;
                      const rowActive =
                        activeCrossValue != null && activeCrossValue === rowValue;
                      // Share-bar row: the composition of THIS row adds
                      // up to 100%, with the headline value beside it.
                      // Segments come from the authored declaration —
                      // order and colour are never inferred from data,
                      // which is what keeps the palette stable when a
                      // bucket is missing from one slice.
                      if (segments && segments.length > 0) {
                        const rowInner = (
                          <>
                            {/* A CEILING, not a fixed width (same rule
                                as MeterBar's labelWidth): as a fixed
                                88px it cut "MERCADOLIBRE", which needs
                                93px and had the room to spare — the
                                card is 282px wide. The label TRACK is
                                the card's longest label, so a card of
                                short labels gives the slack back to
                                every bar at once. */}
                            <span
                              className="min-w-0 max-w-[104px] truncate text-xs font-medium text-neutral-600"
                              style={{ gridColumn: 1 }}
                            >
                              {rowLabel}
                            </span>
                            <div className="min-w-0" style={{ gridColumn: 2 }}>
                              <StackedShareBar
                                segments={segments.map((seg) => ({
                                  key: seg.key,
                                  label: resolveLocalized(seg.label, chartLang) || seg.key,
                                  color: seg.color,
                                  // numOrNull, not toNumber: a missing
                                  // bucket must reach StackedShareBar as
                                  // null so its fail-loud policy fires
                                  // instead of drawing a fabricated 0.
                                  value: numOrNull(row[`seg_${seg.key}`]),
                                }))}
                                height={12}
                                ariaLabel={`${key} ${rowLabel} mix`}
                              />
                            </div>
                            {/* Ronda 4: the row's state is its BAND
                                (90/80 cut-offs), carried by the ToneIcon
                                SHAPE (✓/!/×) so it survives red-green
                                colour-blindness. The % is the single
                                number beside it — the self-history
                                percentile nota died (product decision:
                                compare against the thresholds, never
                                against yourself). */}
                            {v !== null && (
                              <span className="inline-flex" style={{ gridColumn: 3 }}>
                                <ToneIcon
                                  tone={spec ? resolveTone(spec, v) : "neutral"}
                                  size={15}
                                  className="shrink-0"
                                  ariaLabel={`${rowLabel}: ${spec ? resolveTone(spec, v) : "neutral"}`}
                                />
                              </span>
                            )}
                            {v !== null && (
                              <span
                                className="w-[48px] text-right text-xs font-semibold tabular-nums text-neutral-700"
                                style={{ gridColumn: 4 }}
                              >
                                <NumberTween
                                  fromZero
                                  value={v * 100}
                                  durationMs={MOTION.durEntrance}
                        delayMs={MOTION.delayEntrance}
                                  format={(nn) => formatValue(nn / 100, "percent", 1)}
                                />
                              </span>
                            )}
                            {/* Per-row week-over-week delta, mirroring
                                the reference's "94% ▲1.0pp". The numeric
                                `delta` drives the arrow + colour by SIGN
                                (DeltaChip: up = green, down = red — which
                                is correct for an approval share where up
                                is better). The optional `delta_label`
                                carries the unit the component cannot
                                infer (a share row's delta is in
                                percentage POINTS, not percent). Absent
                                delta → nothing renders, so a card grid
                                that does not compute a trend is
                                unchanged. */}
                            {numOrNull(row.delta) !== null && (
                              <span className="inline-flex" style={{ gridColumn: shareLayout.deltaCol }}>
                                <DeltaChip
                                  delta={numOrNull(row.delta) as number}
                                  label={
                                    row.delta_label != null
                                      ? String(row.delta_label)
                                      : undefined
                                  }
                                  className="shrink-0"
                                />
                              </span>
                            )}
                          </>
                        );
                        // A subgrid row of the card's column grid (see
                        // the rows container): its cells land on the
                        // shared tracks, so starts and ends line up.
                        const rowBase = "grid items-center";
                        const rowStyle = SHARE_ROW_STYLE;
                        return rowClickable ? (
                          <button
                            key={rowLabel || i}
                            type="button"
                            aria-pressed={rowActive}
                            onClick={() =>
                              onCrossFilter!(crossFilterKey!, rowValue)
                            }
                            style={rowStyle}
                            className={`${rowBase} group relative -mx-1.5 rounded-lg px-1.5 py-0.5 text-left transition-all ${
                              rowActive
                                ? "bg-accent/10 ring-2 ring-accent/40"
                                : "hover:bg-neutral-50"
                            }`}
                          >
                            {rowInner}
                            {/* Hover pill (ronda 10): the SAME "view
                                detail" affordance drillable table rows
                                use — solid brand magenta, hover-only
                                reveal (the persistent chevron was
                                rejected in PR #61). Tells the reader
                                the retailer row is pressable. */}
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-2xs font-semibold text-white opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100"
                            >
                              {t(
                                rowActive
                                  ? "table.rowdrill.close"
                                  : "table.rowdrill.open",
                              )}
                            </span>
                          </button>
                        ) : (
                          <div key={rowLabel || i} className={rowBase} style={rowStyle}>
                            {rowInner}
                          </div>
                        );
                      }
                      return (
                        <MeterBar
                          key={rowLabel || i}
                          label={rowLabel}
                          labelWidth={96}
                          value={v}
                          max={meterMax ?? 100}
                          toneSpec={spec}
                          showValue
                          valueLabel={row.value_label != null ? String(row.value_label) : undefined}
                          height={6}
                          ariaLabel={`${key} ${rowLabel}`}
                        />
                      );
                    })}
                  </div>
                  {head.group_note != null && String(head.group_note) !== "" && (
                    <Callout
                      className="mt-auto"
                      dense
                      tone={(head.group_note_tone as Tone) ?? "neutral"}
                      title={
                        head.group_note_label != null
                          ? String(head.group_note_label)
                          : undefined
                      }
                    >
                      {String(head.group_note)}
                    </Callout>
                  )}
                </div>
              );
            })}
          </ResponsiveCardGrid>
        </div>
      );
      break;
    }
    // ── Pricing-Brand migration (2026-09-08) ─────────────────
    //
    // Both read a SINGLE raw_sql row whose column names are the
    // contract, like the scorecard arrangements above. They are the
    // two shapes the price-position report needed that the catalog
    // could not draw: "one value against a reference and a band" and
    // "a 100% composition WITH its reading".
    case "index_meter": {
      const head = (data[0] ?? {}) as Record<string, unknown>;
      const bandFrom = numOrNull(head.band_from);
      const meterSub =
        head.subtitle != null && String(head.subtitle) !== ""
          ? String(head.subtitle)
          : subtitle;
      const bandTo = numOrNull(head.band_to);
      const refV = numOrNull(head.reference);
      body = (
        <>
        {meterSub && (
          <p className="mb-1.5 text-xs text-neutral-600">{meterSub}</p>
        )}
        <IndexMeter
          value={numOrNull(head.value) as number}
          min={numOrNull(head.scale_min) as number}
          max={numOrNull(head.scale_max) as number}
          // A band needs BOTH ends measured. One end alone is not a
          // band you can shade — it is a missing number, and inventing
          // the other end is the exact failure this codebase refuses.
          band={
            bandFrom !== null && bandTo !== null
              ? {
                  from: bandFrom,
                  to: bandTo,
                  label: head.band_label != null ? String(head.band_label) : undefined,
                }
              : null
          }
          reference={
            refV !== null
              ? {
                  value: refV,
                  label:
                    head.reference_label != null
                      ? String(head.reference_label)
                      : undefined,
                }
              : null
          }
          unit={head.unit != null ? String(head.unit) : ""}
          note={head.note != null ? String(head.note) : ""}
          // Delta in index points against the period `delta_label` names
          // (G-3). The colour comes ONLY from `delta_good_when`
          // ('up' | 'down'): a direction is not a sentiment for an index.
          // No `delta` column, or a NULL one (no history yet) → no chip.
          delta={numOrNull(head.delta)}
          deltaLabel={head.delta_label != null ? String(head.delta_label) : ""}
          deltaGoodWhen={
            head.delta_good_when === "up" || head.delta_good_when === "down"
              ? head.delta_good_when
              : null
          }
          minLabel={head.min_label != null ? String(head.min_label) : ""}
          maxLabel={head.max_label != null ? String(head.max_label) : ""}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          format={yFormat ? yFmt : undefined}
        />
        </>
      );
      break;
    }
    case "share_breakdown": {
      const head = (data[0] ?? {}) as Record<string, unknown>;
      // Same authored-segments contract as `metric_card_grid`: order
      // and colour come from the CONFIG, never from the data, so a
      // bucket that happens to be absent this snapshot cannot silently
      // repaint the rest of the bar.
      const sbSub =
        head.subtitle != null && String(head.subtitle) !== ""
          ? String(head.subtitle)
          : subtitle;
      // In a grid cell the breakdown fills the whole plot (subtitle
      // included in the budget), so the legend spreads instead of
      // leaving the bottom of the card blank. Outside the grid
      // (plotH null) it keeps the packed form.
      body = (
        <div
          className={plotH != null ? "flex flex-col" : undefined}
          style={plotH != null ? { height: plotH } : undefined}
        >
        {sbSub && <p className="mb-1.5 shrink-0 text-xs text-neutral-600">{sbSub}</p>}
        <div className={plotH != null ? "min-h-0 flex-1" : undefined}>
        <ShareBreakdown
          fill={plotH != null}
          ariaLabel={title || undefined}
          segments={(segments ?? []).map((seg) => ({
            key: seg.key,
            label: resolveLocalized(seg.label, chartLang) || seg.key,
            color: seg.color,
            // numOrNull, not toNumber: an unmeasured bucket must reach
            // the component as null so its fail-loud policy fires.
            value: numOrNull(head[`seg_${seg.key}`]),
          }))}
          total={numOrNull(head.total)}
          totalLabel={head.total_label != null ? String(head.total_label) : ""}
          ofLabel={
            head.of_label != null
              ? String(head.of_label)
              : t("chart.shareBreakdown.of", { defaultValue: "de" })
          }
          height={plotH ?? CHART_HEIGHT}
          // Counts in the app language ("2.124" in ES), not the
          // browser's: the component's default is toLocaleString(undefined).
          format={yFormat ? yFmt : (v: number) => formatNumberLocale(v)}
          // Legend that filters (DSA 1.11): with ``cross_filter_key`` a
          // legend row / bar segment SETS that filter to the segment KEY
          // (same host toggle as the tiles); the active segment is the
          // one whose key equals the filter's value. Without the key the
          // legend is read-only, exactly as before.
          // With ``row_action`` the click also JUMPS (DSA 1.11: clicking a
          // state on Resumen opens Productos filtered to it): the segment
          // key travels as the row's ``action_value``.
          onSegmentClick={
            rowAction
              ? (key: string) => rowAction.onClick({ action_value: key })
              : crossFilterKey && onCrossFilter
                ? (key: string) => onCrossFilter(crossFilterKey, key)
                : undefined
          }
          activeKey={crossFilterKey ? activeCrossValue : undefined}
          clickHint={crossFilterKey && onCrossFilter ? (crossFilterHint ?? t("chart.clickFilter")) : undefined}
        />
        </div>
        </div>
      );
      break;
    }
    case "bar_horizontal": {
      // Same filter as toLabelValue, kept aligned with `data` so a click
      // hands the host the ORIGINAL row; a `tone` column paints that bar
      // in the tone's v3 colour (DSA ring tiers). The SQL never emits a
      // hex: the engine refuses it at publish (look v3, DQ2).
      const barRows = data.filter((d) => d.metric != null && d.metric !== "");
      body = (
        <HorizontalBarT
          ariaLabel={title || undefined}
          data={barRows.map((d) => ({
            label: String(d.dimension ?? ""),
            value: toNumber(d.metric),
            ...(isConfigTone(d.tone) ? { color: toneStyle(d.tone).fill } : {}),
          }))}
          onBarClick={rowAction ? (_label: string, i: number) => rowAction.onClick(barRows[i]!) : undefined}
          clickHint={rowAction?.label}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          density={density as never}
          showShare={showShare}
          format={xFmt}
          dataLabels={dataLabels ?? undefined}
        />
      );
      break;
    }
    case "line":
      body = (
        <LineChartT
          data={toLabelValue(data)}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          format={yFormat ? yFmt : undefined}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
          // Cross-filter por punto (2026-08-10): con ``cross_filter_key``
          // en el config, el clic en un punto setea ese filtro con el
          // label X del punto (p.ej. la semana) — mismo host toggle que
          // los tiles/cards. Sin la key, el chart queda read-only.
          onPointClick={
            crossFilterKey && onCrossFilter
              ? (label: string) => onCrossFilter(crossFilterKey, label)
              : undefined
          }
          activePointLabel={crossFilterKey ? activeCrossValue : undefined}
          clickHint={crossFilterKey && onCrossFilter ? (crossFilterHint ?? t("chart.clickFilter")) : undefined}
        />
      );
      break;
    case "pie":
      body = (
        <PieChartT
          data={toLabelValue(data)}
          colors={palette}
          donut
          height={plotH ?? CHART_HEIGHT}
        />
      );
      break;
    case "grouped_bar":
    case "stacked_bar": {
      const { rows, keys } = toGroupedShape(data);
      const wantStacked = stacked || type === "stacked_bar";
      body = (
        <GroupedBarT
          data={rows}
          keys={keys}
          colors={palette}
          stacked={wantStacked}
          height={plotH ?? CHART_HEIGHT}
          format={yFmt}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    case "diverging_stacked_bar": {
      // Reshape rows from the engine. Each row has a `label` column plus
      // pairs of ``in_<key>`` and ``out_<key>`` columns (one per stack
      // segment). Keys are inferred from columns that match the
      // ``in_<key>`` / ``out_<key>`` prefix, preserving SQL order.
      const inKeys: string[] = [];
      const outKeys: string[] = [];
      const sample = data[0] ?? {};
      for (const col of Object.keys(sample)) {
        if (col.startsWith("in_")) inKeys.push(col.slice(3));
        else if (col.startsWith("out_")) outKeys.push(col.slice(4));
      }
      // Use the intersection in SQL order — segments without a matching
      // counterpart are dropped (a flow that only has inflow but no
      // outflow column would render asymmetrically, which is fine).
      const seenIn = new Set(inKeys);
      const keys = inKeys.filter((k) => outKeys.includes(k) || seenIn.has(k));
      const rows = data.map((d) => {
        const inMap: Record<string, number> = {};
        const outMap: Record<string, number> = {};
        for (const k of keys) {
          inMap[k.toUpperCase()] = toNumber(d[`in_${k}`]);
          outMap[k.toUpperCase()] = toNumber(d[`out_${k}`]);
        }
        return {
          label: String(d.label ?? d.dimension ?? ""),
          in: inMap,
          out: outMap,
        };
      });
      const displayKeys = keys.map((k) => k.toUpperCase());
      // Authored ``segments`` keyed ``in`` / ``out`` / ``net`` name and
      // colour the two halves ("Caídas" / "Recuperaciones"); the net
      // line is drawn only when a ``net`` segment is declared. Without
      // segments: the generic localised prefixes and the net line, as
      // always.
      const segOf = (key: string) => (segments ?? []).find((seg) => seg.key === key);
      const segIn = segOf("in");
      const segOut = segOf("out");
      const segNet = segOf("net");
      const hasSegments = (segments ?? []).length > 0;
      const inflowLabel =
        (segIn && resolveLocalized(segIn.label, chartLang)) ||
        pickLang(chartLang, { en: "Inflow", es: "Entrada", pt: "Entrada" });
      const outflowLabel =
        (segOut && resolveLocalized(segOut.label, chartLang)) ||
        pickLang(chartLang, { en: "Outflow", es: "Salida", pt: "Saída" });
      const netLabel =
        (segNet && resolveLocalized(segNet.label, chartLang)) ||
        pickLang(chartLang, { en: "Net", es: "Neto", pt: "Líquido" });
      body = (
        <DivergingStackedBar
          data={rows}
          keys={displayKeys}
          colors={
            segIn?.color && segOut?.color ? [segIn.color, segOut.color] : palette
          }
          netLine={hasSegments ? Boolean(segNet) : true}
          ariaLabel={title || undefined}
          height={plotH ?? CHART_HEIGHT + 20}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
          inflowLabel={inflowLabel}
          outflowLabel={outflowLabel}
          netLabel={netLabel}
        />
      );
      break;
    }
    case "scatter": {
      const points = data.map((d) => ({
        x: toNumber(d.x),
        y: toNumber(d.y),
        label: d.label != null ? String(d.label) : undefined,
      }));
      body = (
        <ScatterChartT
          data={points}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          xFormat={xFmt}
          yFormat={yFmt}
          dataLabels={dataLabels ?? undefined}
          axisXFormat={_axisXFormat ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    case "bubble": {
      const bubbles = data.map((d) => ({
        x: toNumber(d.x),
        y: toNumber(d.y),
        size: toNumber(d.size),
        label: d.label != null ? String(d.label) : undefined,
      }));
      body = (
        <BubbleChartT
          data={bubbles}
          colors={palette}
          height={plotH ?? CHART_HEIGHT}
          xFormat={xFmt}
          yFormat={yFmt}
          dataLabels={dataLabels ?? undefined}
          axisXFormat={_axisXFormat ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    case "radar": {
      const points = data.map((d) => ({
        axis: String(d.dimension ?? d.axis ?? ""),
        value: toNumber(d.metric ?? d.value),
      }));
      body = (
        <RadarChart
          data={points}
          color={primary}
          height={plotH ?? CHART_HEIGHT + 60}
          dataLabels={dataLabels ?? undefined}
        />
      );
      break;
    }
    case "waterfall": {
      const points = data.map((d) => ({
        label: String(d.dimension ?? d.label ?? ""),
        value: toNumber(d.metric ?? d.value),
        type: typeof d.type === "string" ? d.type : undefined,
      }));
      body = (
        <WaterfallChart
          data={points}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    case "funnel":
      body = (
        <FunnelChart
          data={toLabelValue(data)}
          colors={palette}
          height={plotH ?? CHART_HEIGHT + 40}
          dataLabels={dataLabels ?? undefined}
        />
      );
      break;
    case "treemap":
      body = (
        <TreemapChart
          data={toLabelValue(data)}
          colors={palette}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
        />
      );
      break;
    case "lollipop":
      body = (
        <LollipopChart
          data={toLabelValue(data)}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
        />
      );
      break;
    case "bar_trend":
      body = (
        <BarTrend
          data={toLabelValue(data)}
          color={primary}
          trendColor={palette[3] ?? palette[1]!}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    case "line_trend":
      body = (
        <LineTrend
          data={toLabelValue(data)}
          color={primary}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    case "multi_line": {
      // Two data formats supported:
      //  - WIDE: one row per dimension, one column per series
      //    (e.g. {dimension, "No Autorizado", "Autorizado", "1P", "HHO"}).
      //    This is the legacy MPA-trend shape used by gating_market.
      //  - LONG: one row per (dimension, series) with a "series" and
      //    "value" column. Used when series names are dynamic and not
      //    known at config time (e.g. ASIN names per SKU in
      //    gating_catalog). Long format works around DuckDB's
      //    "PIVOT can't have parameters in source" restriction.
      const firstRow = data[0]!;
      const isLong =
        firstRow !== undefined &&
        "series" in firstRow &&
        "value" in firstRow;
      let labels: string[];
      let series: { name: string; data: (number | null)[]; dashed?: boolean }[];
      if (isLong) {
        const labelOrder: string[] = [];
        const seen = new Set<string>();
        const bySeries = new Map<string, Map<string, number | null>>();
        // A row may mark its series `style: "dashed"` (a previous-period
        // reference line). Series names may be SQL-built bilingual JSON.
        const dashedSeries = new Set<string>();
        for (const d of data) {
          const lbl = String(d.dimension ?? d.label ?? "");
          if (!seen.has(lbl)) {
            seen.add(lbl);
            labelOrder.push(lbl);
          }
          const seriesName = String(d.series ?? "");
          if (!bySeries.has(seriesName)) bySeries.set(seriesName, new Map());
          bySeries.get(seriesName)!.set(lbl, numOrNull(d.value));
          if (d.style === "dashed") dashedSeries.add(seriesName);
        }
        labels = labelOrder;
        series = [...bySeries.entries()].map(([name, valMap]) => ({
          name: resolveLocalized(name as DisplayLabel, chartLang) || name,
          ...(dashedSeries.has(name) ? { dashed: true } : {}),
          // A label this series has no row for is a GAP, not a zero.
          // `?? 0` made the line dive to the axis and come back, which
          // reads as a collapse that never happened. MultiLineT splits
          // its path on non-numbers, so null renders as a break.
          data: labels.map((l) => valMap.get(l) ?? null),
        }));
      } else {
        labels = data.map((d) => String(d.dimension ?? d.label ?? ""));
        const seriesNames = Object.keys(firstRow).filter(
          (k) => k !== "dimension" && k !== "label",
        );
        series = seriesNames.map((name) => ({
          name,
          data: data.map((d) => numOrNull(d[name])),
        }));
      }
      body = (
        <MultiLineT
          ariaLabel={title || undefined}
          series={series}
          labels={labels}
          legendToggle={legendToggle}
          onPointClick={
            crossFilterKey && onCrossFilter
              ? (label: string) => onCrossFilter(crossFilterKey, label)
              : undefined
          }
          activePointLabel={crossFilterKey ? activeCrossValue : undefined}
          clickHint={crossFilterKey && onCrossFilter ? (crossFilterHint ?? t("chart.clickFilter")) : undefined}
          legendDeltas={legendDeltas}
          tooltipTotal={tooltipTotal}
          colors={palette}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
          dualAxis={dualAxis ?? undefined}
          yMin={axisYFormat?.min}
          yMax={axisYFormat?.max}
          yStep={axisYFormat?.step}
          ySuffix={axisYFormat?.suffix}
          y1Min={axisY1Format?.min}
          y1Max={axisY1Format?.max}
          y1Step={axisY1Format?.step}
          y1Suffix={axisY1Format?.suffix}
        />
      );
      break;
    }
    case "stacked_area": {
      const labels = data.map((d) => String(d.dimension ?? d.label ?? ""));
      const firstRow = data[0]!;
      const columns = Object.keys(firstRow).filter(
        (k) => k !== "dimension" && k !== "label",
      );
      // Authored ``segments`` (keyed by SQL column) give each series its
      // order, bilingual name and colour — the same contract as
      // ``share_breakdown``, so a column alias never reaches the legend
      // in the wrong language. Columns without a segment follow.
      const areaSegs = (segments ?? []).filter((seg) => columns.includes(seg.key));
      const ordered = [
        ...areaSegs.map((seg) => seg.key),
        ...columns.filter((col) => !areaSegs.some((seg) => seg.key === col)),
      ];
      const series = ordered.map((col) => {
        const seg = areaSegs.find((sg) => sg.key === col);
        return {
          name: seg
            ? resolveLocalized(seg.label, chartLang) || col
            : resolveLocalized(col as DisplayLabel, chartLang) || col,
          data: data.map((d) => toNumber(d[col])),
        };
      });
      const areaColors =
        areaSegs.length > 0
          ? ordered.map(
              (col, i) =>
                areaSegs.find((sg) => sg.key === col)?.color || palette[i % palette.length]!,
            )
          : palette;
      body = (
        <StackedArea
          series={series}
          labels={labels}
          colors={areaColors}
          ariaLabel={title || undefined}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
        />
      );
      break;
    }
    case "combo": {
      const firstRow = data[0]!;
      const metricKeys = Object.keys(firstRow).filter(
        (k) => k !== "dimension" && k !== "label",
      );
      const barKey = metricKeys[0] ?? "metric";
      const lineKey = metricKeys[1] ?? "metric2";
      const rows = data.map((d) => ({
        label: String(d.dimension ?? d.label ?? ""),
        [barKey]: toNumber(d[barKey]),
        [lineKey]: toNumber(d[lineKey]),
      }));
      // Heuristic: if the line column name contains "%" the right-axis
      // values are percentages — append a "%" suffix so the labels read
      // as "62%" / "60%" / ... instead of bare "62" / "60".
      const lineIsPercent = /%/.test(lineKey);
      body = (
        <ComboChartT
          data={rows}
          barKey={barKey}
          lineKey={lineKey}
          barLabel={barKey}
          lineLabel={lineKey}
          barColor={palette[0]!}
          lineColor={palette[2] ?? palette[1]!}
          height={plotH ?? CHART_HEIGHT}
          dataLabels={dataLabels ?? undefined}
          axisYFormat={axisYFormat ?? undefined}
          lineSuffix={lineIsPercent ? "%" : undefined as never}
        />
      );
      break;
    }
    case "heatmap": {
      const rowLabels = Array.from(new Set(data.map((d) => String(d.row ?? d.dimension ?? ""))));
      const colLabels = Array.from(new Set(data.map((d) => String(d.col ?? d.column ?? ""))));
      const matrix = rowLabels.map((r) =>
        colLabels.map((c) => {
          const cell = data.find(
            (d) => String(d.row ?? d.dimension) === r && String(d.col ?? d.column) === c,
          );
          // Absent cell → null. As 0 it took one end of the heat ramp,
          // so "no reading" and "lowest reading" were the same colour.
          return cell ? toNumber(cell.value ?? cell.metric) : null;
        }),
      );
      body = (
        <HeatmapChart
          ariaLabel={title || undefined}
          data={matrix}
          // The component's axes are xLabels/yLabels. These were passed
          // as rows/cols, so `xLabels` arrived undefined and
          // `xLabels.length` threw — a TypeError that white-screened
          // the whole page for any report using this type (dashboard
          // included). Columns are the x axis, rows the y axis.
          xLabels={colLabels}
          yLabels={rowLabels}
          height={plotH ?? CHART_HEIGHT}
          format={yFormat ? yFmt : undefined}
          onCellClick={
            rowAction
              ? (xi: number, yi: number) => {
                  const cell = data.find(
                    (d) =>
                      String(d.row ?? d.dimension) === rowLabels[yi] &&
                      String(d.col ?? d.column) === colLabels[xi],
                  );
                  if (cell) rowAction.onClick(cell);
                }
              : undefined
          }
          clickHint={rowAction?.label}
        />
      );
      break;
    }
    case "bullet": {
      const points = data.map((d) => ({
        label: String(d.dimension ?? d.label ?? ""),
        value: toNumber(d.metric ?? d.value),
        // presence-preserving: a missing target/max reaches BulletChart as null → error state, never 0
        target: numOrNull(d.target),
        max: numOrNull(d.max),
      }));
      // Dashboards colour a below-target bullet with the DEEP band red
      // (colour-blind-friendly pair vs the teal hit — ronda 4). The
      // component's own default stays amber for standalone use.
      body = (
        <BulletChart
          data={points}
          height={plotH ?? CHART_HEIGHT - 40}
          missColor={SEMANTIC.bandNegative}
          missingTargetMaxLabel={t("dashboard.chart.bulletMissingTargetMax")}
        />
      );
      break;
    }
    case "map": {
      // Phase E — geo choropleth. Reshape `[{dimension, metric}]` rows
      // into the `{ <ISO>: { value: '$1.2K', numValue: 1234 } }` shape
      // that SouthAmericaMap / ChileMap consume. Variant comes from
      // `props.map_variant`; default = `south_america`.
      const mapData: Record<string, { value: string; numValue: number }> = {};
      for (const d of data) {
        const code = String(d.dimension ?? "").toUpperCase();
        if (!code) continue;
        // A NULL metric is "no data", not 0 (DEC-10): leaving the region
        // out of `mapData` makes the map draw it hatched with the
        // "no data" legend entry. `toNumber` would have turned it into 0
        // and painted it as the low end of the ramp.
        if (d.metric === null || d.metric === undefined || d.metric === "") continue;
        const num = toNumber(d.metric);
        mapData[code] = {
          value: formatCellValue(num, "currency"),
          numValue: num,
        };
      }
      const variant = mapVariant === "chile" ? "chile" : "south_america";
      const mapStrings = {
        scale: t("dashboard.chart.map.scale"),
        low: t("dashboard.chart.map.low"),
        high: t("dashboard.chart.map.high"),
        noData: t("dashboard.chart.map.noData"),
        loading: t("dashboard.chart.map.loading"),
        loadError: t("dashboard.chart.mapLoadError"),
      };
      // Maps render their own card/title — no outer ChartCard needed.
      // In fill mode the map body height is the cell minus the MAP's
      // own chrome (not ChartCard's) so the whole card lands exactly
      // on its grid cell. (`cellPx != null` — not `fill` — so TS
      // narrows the operand; the two are the same condition.)
      const mapH =
        cellPx != null
          ? Math.max(MIN_PLOT_PX, cellPx - MAP_CARD_CHROME_PX)
          : null;
      return variant === "chile" ? (
        <ChileMap
          data={mapData}
          title={title}
          height={mapH ?? CHART_HEIGHT + 200}
          strings={{ ...mapStrings, heading: t("dashboard.chart.map.byRegion") }}
        />
      ) : (
        <SouthAmericaMap
          data={mapData}
          title={title}
          height={mapH ?? CHART_HEIGHT + 100}
          strings={{ ...mapStrings, heading: t("dashboard.chart.map.byCountry") }}
        />
      );
    }
    case "timeline": {
      // Phase G — gantt-style. Backend returns rows shaped
      // `[{label, start, end, progress}]`.
      const points = data.map((d) => ({
        label: String(d.label ?? d.dimension ?? ""),
        start: toNumber(d.start),
        end: toNumber(d.end),
        progress:
          d.progress != null && d.progress !== ""
            ? Math.min(Math.max(toNumber(d.progress), 0), 1)
            : undefined,
      }));
      const monthsRaw = (timelineMonths ?? "").trim();
      const months = monthsRaw
        ? monthsRaw
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        : [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
      // Fill mode passes the derived plot height (review #6): the
      // timeline's viewBox layout is fully proportional, so a small
      // cell renders the COMPLETE gantt smaller instead of clipping
      // rows. Legacy mode keeps the natural 200px default.
      body = (
        <TimelineChart data={points} months={months} height={plotH ?? undefined} />
      );
      break;
    }
    case "calendar_heatmap": {
      // Phase F — temporal density. Reshape `[{dimension=date, metric}]`
      // into the `{ 'YYYY-MM-DD': value }` record the bi-components component takes.
      // Year preference: explicit prop > first parseable year in data >
      // current year.
      const points = data.map((d) => ({
        date: String(d.dimension ?? ""),
        value: toNumber(d.metric),
      }));
      const explicitYear =
        calendarYear == null || calendarYear === ""
          ? null
          : Number(calendarYear);
      const inferred =
        points
          .map((p) => Number(p.date.slice(0, 4)))
          .find((y) => Number.isFinite(y)) ?? new Date().getFullYear();
      const year =
        explicitYear != null && Number.isFinite(explicitYear)
          ? explicitYear
          : inferred;
      // CalendarHeatmap reads a Record keyed 'YYYY-MM-DD' (analytics.jsx:70),
      // not the [{date,value}] array (audit #216). A timestamp dimension is cut
      // to its day. Two rows for the SAME day fail loud: keeping one would
      // silently drop the other's value.
      const byDay = new Map<string, number>();
      for (const p of points) {
        const day = p.date.slice(0, 10);
        if (byDay.has(day)) {
          return (
            <MessageCard title={title} fill={fill} tone="error">
              {t("dashboard.chart.calendarDuplicateDay", { day })}
            </MessageCard>
          );
        }
        byDay.set(day, p.value);
      }
      // Fill mode passes the derived plot height (review #6): the
      // calendar letterboxes (fixed natural viewBox, see
      // bi-components/analytics.jsx) so a small cell shows the COMPLETE
      // year scaled down instead of clipping day cells. Legacy mode
      // keeps the natural 140px default.
      body = (
        <CalendarHeatmap
          data={Object.fromEntries(byDay)}
          year={year}
          height={plotH ?? undefined}
          locale={chartLang}
          strings={{
            less: t("dashboard.chart.calendar.less"),
            more: t("dashboard.chart.calendar.more"),
          }}
        />
      );
      break;
    }
    case "stacked_bar_legacy": {
      const { rows, keys } = toGroupedShape(data);
      body = (
        <StackedBar data={rows} keys={keys} colors={palette} height={plotH ?? CHART_HEIGHT} />
      );
      break;
    }
    default:
      // FAIL LOUD (epic v3). This used to fall back to a VerticalBar,
      // which is the most dangerous possible default: a config whose
      // chart_type the deployed code does not know rendered a
      // plausible-but-WRONG bar chart of whatever columns happened to
      // reshape, with no error anywhere — to an executive audience.
      //
      // Safe to flip because every chart_type present in staging AND
      // production configs was enumerated first (10 types: bar,
      // bar_horizontal, bubble, combo, diverging_stacked_bar,
      // grouped_bar, line, multi_line, scatter, table) and each one is
      // either a case above or the `table` early return.
      return (
        <MessageCard title={title} fill={fill} tone="error">
          {t("dashboard.chart.unknownType", {
            defaultValue: "Unknown chart type: {{type}}",
            type: String(type ?? ""),
          })}
        </MessageCard>
      );
  }

  // viewBox-based charts keep their natural aspect (letterboxed) — in
  // fill mode they center vertically inside the cell instead of pinning
  // to the top. No re-architecture of the SVGs (documented limitation:
  // give these types an `h` close to their natural height).
  const centerInCell = type === "timeline" || type === "calendar_heatmap";

  // Cast around React 18/19 ReactNode drift between this app's @types/react
  // and the workspace package's resolution (the package's `ReactNode` type
  // resolves to a different copy than ours in some npm install layouts).
  // Authored subtitle, opt-in (`subtitle_inline`). `ChartCard` has
  // always taken a subtitle and `GenericChart` has never passed one,
  // because the header is sized at exactly CHART_CARD_CHROME_PX (62):
  // a second header line pushes the plot ~18px past its grid cell, and
  // several shipped dashboards already declare subtitles that render
  // nowhere today. So it lands INSIDE the body, only when asked for,
  // and the author pays for it with one extra row unit.
  //
  // The two Pricing-Brand types lead with a subtitle their own SQL row
  // carries, so they must not get a second copy here. `metric_strip`
  // and `ranked_list` draw their own too — they RETURN before this
  // point, which is why naming them here is a type error rather than a
  // guard (the compiler has already narrowed them out).
  const ownsSubtitle = type === "index_meter" || type === "share_breakdown";
  const inlineSubtitle =
    subtitleInline && subtitle && !ownsSubtitle ? subtitle : null;

  // In-chart controls ride the card header's `action` slot (one line,
  // so CHART_CARD_CHROME_PX holds); a chart without controls renders
  // the plain card.
  const controlsNode =
    controls && controls.length > 0 ? (
      <div data-chart-controls className="flex flex-wrap justify-end gap-2">
        {controls.map((ctrl) => (
          <div
            key={ctrl.key}
            role="group"
            aria-label={ctrl.label || ctrl.key}
            className="flex overflow-hidden rounded-[10px] border border-neutral-300"
          >
            {ctrl.options.map((o) => {
              const on = ctrl.active === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => ctrl.onSelect(o.value)}
                  className={`px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 ${
                    on ? "bg-navy text-white" : "bg-white text-neutral-700 hover:bg-primary-soft"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    ) : null;
  const cardBody = (
    <>
      {inlineSubtitle && (
        <p className="mb-1.5 shrink-0 text-xs text-neutral-600">
          {inlineSubtitle}
        </p>
      )}
      {
        (fill && centerInCell ? (
          <div className="flex-1 min-h-0 flex flex-col justify-center">
            {body}
          </div>
        ) : (
          body
        )) as never
      }
    </>
  );
  return controlsNode ? (
    <ChartCard title={title} info={info} fill={fill} action={controlsNode}>
      {cardBody}
    </ChartCard>
  ) : (
    <ChartCard title={title} info={info} fill={fill}>
      {cardBody}
    </ChartCard>
  );
}
